import { type RefObject, useEffect } from 'react'

interface UseIntersectionTriggerOptions {
  target: RefObject<Element | null>
  onIntersect?: () => void
  enabled?: boolean
  canTrigger?: boolean
  threshold?: number
  root?: Element | Document | null
  rootMargin?: string
}

export function useIntersectionTrigger({
  target,
  onIntersect,
  enabled = true,
  canTrigger = true,
  threshold = 0.1,
  root,
  rootMargin,
}: UseIntersectionTriggerOptions) {
  useEffect(() => {
    if (!enabled || !onIntersect || !canTrigger) {
      return
    }

    const observedTarget = target.current
    if (!observedTarget) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onIntersect()
        }
      },
      { threshold, root: root ?? null, rootMargin },
    )

    observer.observe(observedTarget)

    return () => {
      observer.unobserve(observedTarget)
    }
  }, [canTrigger, enabled, onIntersect, root, rootMargin, target, threshold])
}
